import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  CalendarClock,
  Clock,
  Video,
  Phone,
  MapPin,
  Type,
  Plus,
  Trash2,
  Copy,
  ExternalLink,
  Check,
  X,
  Loader2,
  MessageSquare,
  Palette,
  Settings2,
  ChevronRight,
  User,
} from "lucide-react";
import { bookingTypesApi } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect from "../ui/SearchableSelect";
import Toggle from "../ui/Toggle";
import TimePicker from "../ui/TimePicker";

// ── Helpers ──────────────────────────────────────────────────────────

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function formatTime12(time24: string): string {
  if (!time24) return "";
  const [h, m] = time24.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m.toString().padStart(2, "0")} ${ampm}`;
}

// ── Constants ────────────────────────────────────────────────────────

const DURATION_PRESETS = [15, 30, 45, 60, 90, 120];

const LOCATION_TYPES = [
  { value: "video", label: "Video", icon: Video },
  { value: "phone", label: "Phone", icon: Phone },
  { value: "in_person", label: "In-Person", icon: MapPin },
  { value: "custom", label: "Custom", icon: Type },
] as const;

const DAY_LABELS = [
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
  { key: "sunday", label: "Sun" },
] as const;

const TIMEZONE_OPTIONS = [
  { value: "America/Toronto", label: "America/Toronto (EST/EDT)" },
  { value: "America/New_York", label: "America/New_York (EST/EDT)" },
  { value: "America/Chicago", label: "America/Chicago (CST/CDT)" },
  { value: "America/Denver", label: "America/Denver (MST/MDT)" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles (PST/PDT)" },
  { value: "America/Vancouver", label: "America/Vancouver (PST/PDT)" },
  { value: "America/Edmonton", label: "America/Edmonton (MST/MDT)" },
  { value: "America/Winnipeg", label: "America/Winnipeg (CST/CDT)" },
  { value: "America/Halifax", label: "America/Halifax (AST/ADT)" },
  { value: "America/St_Johns", label: "America/St_Johns (NST/NDT)" },
  { value: "Europe/London", label: "Europe/London (GMT/BST)" },
  { value: "Europe/Paris", label: "Europe/Paris (CET/CEST)" },
  { value: "UTC", label: "UTC" },
];

const PRESET_COLORS = [
  "#3B82F6",
  "#EF4444",
  "#8B5CF6",
  "#F59E0B",
  "#10B981",
  "#EC4899",
  "#06B6D4",
  "#6366F1",
];

const QUESTION_TYPES = [
  { value: "text", label: "Text" },
  { value: "textarea", label: "Textarea" },
  { value: "select", label: "Dropdown" },
];

const STEP_LABELS = ["Basics", "Availability", "Questions", "Review"];

// ── Types ────────────────────────────────────────────────────────────

interface TimeSlot {
  startTime: string;
  endTime: string;
}

interface DaySchedule {
  enabled: boolean;
  slots: TimeSlot[];
}

interface CustomQuestion {
  id: string;
  label: string;
  type: "text" | "textarea" | "select";
  required: boolean;
  options: string[];
}

interface FormState {
  name: string;
  slug: string;
  duration: number;
  customDuration: boolean;
  description: string;
  locationType: string;
  locationDetails: string;
  availability: Record<string, DaySchedule>;
  checkCalendarConflicts: boolean;
  bufferBefore: number;
  bufferAfter: number;
  minimumNotice: number;
  rollingWindow: number;
  maxBookingsPerDay: string;
  timezone: string;
  collectPhone: boolean;
  collectMessage: boolean;
  questions: CustomQuestion[];
  accentColor: string | null;
  confirmationMessage: string;
  requiresApproval: boolean;
}

const SUGGESTED_QUESTIONS: { label: string; type: "text" | "textarea" | "select"; required: boolean; options?: string[] }[] = [
  { label: "What is the nature of your legal matter?", type: "select", required: true, options: ["Family Law", "Real Estate", "Personal Injury", "Criminal Defence", "Corporate/Business", "Immigration", "Estate Planning", "Employment", "Other"] },
  { label: "Have you spoken with another lawyer about this matter?", type: "select", required: false, options: ["Yes", "No"] },
  { label: "What is the approximate timeline for your matter?", type: "select", required: false, options: ["Urgent (within a week)", "Soon (1–4 weeks)", "Not urgent (1–3 months)", "Just exploring options"] },
  { label: "How did you hear about us?", type: "select", required: false, options: ["Google Search", "Referral", "Social Media", "Website", "Other"] },
  { label: "Is there anything else we should know before the meeting?", type: "textarea", required: false },
];

// Day name → dayOfWeek number (0=Sun, 1=Mon, etc.)
const DAY_NAME_TO_DOW: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const DOW_TO_DAY_NAME: Record<number, string> = Object.fromEntries(
  Object.entries(DAY_NAME_TO_DOW).map(([k, v]) => [v, k])
);

interface BookingTypeModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (created?: { name: string; slug: string }) => void;
  editId?: string | null;
}

// ── Default Values ───────────────────────────────────────────────────

function buildDefaultAvailability(): Record<string, DaySchedule> {
  const schedule: Record<string, DaySchedule> = {};
  for (const day of DAY_LABELS) {
    const isWeekday = !["saturday", "sunday"].includes(day.key);
    schedule[day.key] = {
      enabled: isWeekday,
      slots: [{ startTime: "09:00", endTime: "17:00" }],
    };
  }
  return schedule;
}

/** Convert backend availability array → frontend Record<dayName, DaySchedule> */
function backendToFormAvailability(
  arr: { dayOfWeek: number; startTime: string; endTime: string }[]
): Record<string, DaySchedule> {
  const base = buildDefaultAvailability();
  // Disable all first, then enable only those present in backend data
  for (const day of DAY_LABELS) {
    base[day.key] = { enabled: false, slots: [{ startTime: "09:00", endTime: "17:00" }] };
  }
  for (const entry of arr) {
    const name = DOW_TO_DAY_NAME[entry.dayOfWeek];
    if (!name) continue;
    if (!base[name].enabled) {
      base[name] = { enabled: true, slots: [] };
    }
    base[name].slots.push({
      startTime: entry.startTime,
      endTime: entry.endTime,
    });
  }
  return base;
}

/** Convert frontend Record<dayName, DaySchedule> → backend array */
function formToBackendAvailability(
  avail: Record<string, DaySchedule>
): { dayOfWeek: number; startTime: string; endTime: string }[] {
  const result: { dayOfWeek: number; startTime: string; endTime: string }[] = [];
  for (const [dayName, schedule] of Object.entries(avail)) {
    if (!schedule.enabled) continue;
    const dow = DAY_NAME_TO_DOW[dayName];
    if (dow === undefined) continue;
    for (const slot of schedule.slots) {
      result.push({
        dayOfWeek: dow,
        startTime: slot.startTime,
        endTime: slot.endTime,
      });
    }
  }
  return result;
}

function buildDefaultForm(): FormState {
  return {
    name: "",
    slug: "",
    duration: 30,
    customDuration: false,
    description: "",
    locationType: "video",
    locationDetails: "",
    availability: buildDefaultAvailability(),
    checkCalendarConflicts: true,
    bufferBefore: 0,
    bufferAfter: 0,
    minimumNotice: 1,
    rollingWindow: 60,
    maxBookingsPerDay: "",
    timezone: "America/Toronto",
    collectPhone: true,
    collectMessage: true,
    questions: [],
    accentColor: null,
    confirmationMessage: "",
    requiresApproval: false,
  };
}

// ── Component ────────────────────────────────────────────────────────

const BookingTypeModal: React.FC<BookingTypeModalProps> = ({
  open,
  onClose,
  onSave,
  editId,
}) => {
  const { firmData } = useUserDataContext();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormState>(buildDefaultForm());
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [copied, setCopied] = useState(false);
  const slugCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isEditing = !!editId;

  // ── Form updater ───────────────────────────────────────────────────

  const updateForm = useCallback((updates: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...updates }));
  }, []);

  // ── Load existing booking type for editing ─────────────────────────

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSlugError(null);
    setSlugManuallyEdited(false);
    setCopied(false);

    if (editId) {
      setLoading(true);
      bookingTypesApi
        .get(editId)
        .then((res) => {
          const data = res.data?.data || res.data;
          setForm({
            name: data.name || "",
            slug: data.slug || "",
            duration: data.duration || 30,
            customDuration: !DURATION_PRESETS.includes(data.duration),
            description: data.description || "",
            locationType: data.location?.type || data.locationType || "video",
            locationDetails: data.location?.details || data.locationDetails || "",
            availability: Array.isArray(data.availability)
              ? backendToFormAvailability(data.availability)
              : buildDefaultAvailability(),
            checkCalendarConflicts: data.checkCalendarConflicts !== false,
            bufferBefore: data.bufferBefore || 0,
            bufferAfter: data.bufferAfter || 0,
            minimumNotice: data.minimumNotice || 1,
            rollingWindow: data.rollingWindow || 60,
            maxBookingsPerDay:
              data.maxBookingsPerDay != null
                ? String(data.maxBookingsPerDay)
                : "",
            timezone: data.timezone || "America/Toronto",
            collectPhone: data.collectPhone !== false,
            collectMessage: data.collectMessage !== false,
            questions: (data.questions || []).map((q: any, i: number) => ({
              id: q.id || `q_${Date.now()}_${i}`,
              label: q.label || "",
              type: q.type || "text",
              required: q.required ?? false,
              options: q.options || [],
            })),
            accentColor: data.accentColor || null,
            confirmationMessage: data.confirmationMessage || "",
            requiresApproval: data.requiresApproval || false,
          });
          setSlugManuallyEdited(true);
        })
        .catch((err) => {
          console.error("Failed to load booking type:", err);
        })
        .finally(() => setLoading(false));
    } else {
      setForm(buildDefaultForm());
    }
  }, [open, editId]);

  // ── Auto-generate slug from name ──────────────────────────────────

  useEffect(() => {
    if (!slugManuallyEdited && form.name) {
      updateForm({ slug: slugify(form.name) });
    }
  }, [form.name, slugManuallyEdited, updateForm]);

  // ── Debounced slug uniqueness check ────────────────────────────────

  useEffect(() => {
    if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current);
    const slug = form.slug.trim();
    if (!slug) {
      setSlugError(null);
      setSlugChecking(false);
      return;
    }

    setSlugChecking(true);
    slugCheckTimer.current = setTimeout(async () => {
      try {
        const res = await bookingTypesApi.checkSlug(slug, editId || undefined);
        if (!res.data.available) {
          setSlugError("This URL slug is already taken. Please choose a different one.");
        } else {
          setSlugError(null);
        }
      } catch {
        // Silently ignore check failures — save handler will catch duplicates
      } finally {
        setSlugChecking(false);
      }
    }, 400);

    return () => {
      if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current);
    };
  }, [form.slug, editId]);

  // ── Save handler ──────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    setSlugError(null);

    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim(),
      duration: form.duration,
      description: form.description.trim() || null,
      location: {
        type: form.locationType,
        details: form.locationDetails.trim() || null,
      },
      availability: formToBackendAvailability(form.availability),
      checkCalendarConflicts: form.checkCalendarConflicts,
      bufferBefore: form.bufferBefore,
      bufferAfter: form.bufferAfter,
      minimumNotice: form.minimumNotice,
      rollingWindow: form.rollingWindow,
      maxBookingsPerDay: form.maxBookingsPerDay
        ? parseInt(form.maxBookingsPerDay, 10)
        : null,
      timezone: form.timezone,
      collectPhone: form.collectPhone,
      collectMessage: form.collectMessage,
      questions: form.questions.map((q) => ({
        label: q.label,
        type: q.type,
        required: q.required,
        options: q.type === "select" ? q.options : [],
      })),
      accentColor: form.accentColor,
      confirmationMessage: form.confirmationMessage.trim() || null,
      requiresApproval: form.requiresApproval,
    };

    try {
      if (isEditing) {
        await bookingTypesApi.update(editId!, payload);
        onSave();
      } else {
        await bookingTypesApi.create(payload);
        onSave({ name: payload.name, slug: payload.slug });
      }
      onClose();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setSlugError(
          "This URL slug is already taken. Please choose a different one."
        );
        setStep(1);
      } else {
        console.error("Failed to save booking type:", err);
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Question helpers ──────────────────────────────────────────────

  const addQuestion = () => {
    updateForm({
      questions: [
        ...form.questions,
        {
          id: `q_${Date.now()}`,
          label: "",
          type: "text",
          required: false,
          options: [],
        },
      ],
    });
  };

  const updateQuestion = (id: string, updates: Partial<CustomQuestion>) => {
    updateForm({
      questions: form.questions.map((q) =>
        q.id === id ? { ...q, ...updates } : q
      ),
    });
  };

  const removeQuestion = (id: string) => {
    updateForm({ questions: form.questions.filter((q) => q.id !== id) });
  };

  const addQuestionOption = (questionId: string) => {
    updateForm({
      questions: form.questions.map((q) =>
        q.id === questionId ? { ...q, options: [...q.options, ""] } : q
      ),
    });
  };

  const updateQuestionOption = (
    questionId: string,
    index: number,
    value: string
  ) => {
    updateForm({
      questions: form.questions.map((q) =>
        q.id === questionId
          ? {
              ...q,
              options: q.options.map((o, i) => (i === index ? value : o)),
            }
          : q
      ),
    });
  };

  const removeQuestionOption = (questionId: string, index: number) => {
    updateForm({
      questions: form.questions.map((q) =>
        q.id === questionId
          ? { ...q, options: q.options.filter((_, i) => i !== index) }
          : q
      ),
    });
  };

  // ── Copy booking link ─────────────────────────────────────────────

  const firmSlug = firmData.slug || firmData._id;
  const bookingUrl = `${window.location.origin}/book/${firmSlug}/${form.slug || "your-slug"}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(bookingUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Availability summary for review ───────────────────────────────

  const availabilitySummary = (() => {
    const enabledDays = DAY_LABELS.filter(
      (d) => form.availability[d.key]?.enabled
    );
    if (enabledDays.length === 0) return "No availability set";

    // Check if all days have the same single slot
    const allSameTime = enabledDays.every((d) => {
      const slots = form.availability[d.key].slots;
      const firstSlots = form.availability[enabledDays[0].key].slots;
      return (
        slots.length === 1 &&
        firstSlots.length === 1 &&
        slots[0].startTime === firstSlots[0].startTime &&
        slots[0].endTime === firstSlots[0].endTime
      );
    });

    const dayRange = enabledDays.map((d) => d.label).join(", ");
    if (allSameTime && enabledDays[0]) {
      const slot = form.availability[enabledDays[0].key].slots[0];
      return `${dayRange}, ${formatTime12(slot.startTime)} - ${formatTime12(slot.endTime)}`;
    }
    return `${dayRange} (varied hours)`;
  })();

  // ── Validation ────────────────────────────────────────────────────

  const canAdvance = (() => {
    if (step === 1) return form.name.trim().length > 0 && form.slug.trim().length > 0 && !slugError && !slugChecking;
    if (step === 2)
      return DAY_LABELS.some((d) => form.availability[d.key]?.enabled);
    return true;
  })();

  // ── Stepper ───────────────────────────────────────────────────────

  const renderStepper = () => (
    <div className="flex items-center justify-center mb-6">
      {STEP_LABELS.map((label, i) => {
        const stepNum = i + 1;
        const isActive = step === stepNum;
        const isCompleted = step > stepNum;
        return (
          <React.Fragment key={label}>
            {i > 0 && (
              <div
                className={`h-0.5 w-8 sm:w-12 mx-1 rounded-full transition-colors ${
                  isCompleted
                    ? "bg-emerald-500"
                    : "bg-slate-200"
                }`}
              />
            )}
            <div className="flex flex-col items-center gap-1">
              <div
                className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  isActive
                    ? "text-white shadow-md"
                    : isCompleted
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-slate-100 text-slate-400"
                }`}
                style={
                  isActive
                    ? { backgroundColor: "var(--color-primary)" }
                    : {}
                }
              >
                {isCompleted ? (
                  <Check className="h-4 w-4" />
                ) : (
                  stepNum
                )}
              </div>
              <span
                className={`text-xs font-medium hidden sm:block ${
                  isActive
                    ? "text-slate-800"
                    : isCompleted
                    ? "text-emerald-700"
                    : "text-slate-400"
                }`}
              >
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  // ── Step 1: Basics ────────────────────────────────────────────────

  const renderStep1 = () => (
    <div className="space-y-5">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          required
          value={form.name}
          onChange={(e) => updateForm({ name: e.target.value })}
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 transition-colors"
          placeholder="e.g., Initial Consultation"
          autoFocus
        />
      </div>

      {/* Slug */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          URL Slug <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            required
            value={form.slug}
            onChange={(e) => {
              setSlugManuallyEdited(true);
              updateForm({ slug: slugify(e.target.value) });
            }}
            className={`w-full rounded-xl border px-4 py-3 pr-10 text-sm focus:outline-none focus:ring-2 transition-colors ${
              slugError
                ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                : "border-slate-200 focus:border-emerald-600 focus:ring-emerald-100"
            }`}
            placeholder="initial-consultation"
          />
          {form.slug.trim() && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              {slugChecking ? (
                <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />
              ) : slugError ? (
                <X className="h-4 w-4 text-red-500" />
              ) : (
                <Check className="h-4 w-4 text-emerald-500" />
              )}
            </span>
          )}
        </div>
        {slugError ? (
          <p className="mt-1.5 text-xs text-red-600">{slugError}</p>
        ) : (
          <p className="mt-1.5 text-xs text-slate-400">
            {bookingUrl}
          </p>
        )}
      </div>

      {/* Duration */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Duration
        </label>
        <div className="flex flex-wrap gap-2">
          {DURATION_PRESETS.map((mins) => {
            const selected = !form.customDuration && form.duration === mins;
            return (
              <button
                key={mins}
                type="button"
                onClick={() =>
                  updateForm({
                    duration: mins,
                    customDuration: false,
                  })
                }
                className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${
                  selected
                    ? "text-white border-transparent shadow-sm"
                    : "border-slate-200 text-slate-600 hover:border-slate-300"
                }`}
                style={
                  selected
                    ? { backgroundColor: "var(--color-primary)" }
                    : {}
                }
              >
                {mins} min
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => updateForm({ customDuration: true })}
            className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${
              form.customDuration
                ? "text-white border-transparent shadow-sm"
                : "border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
            style={
              form.customDuration
                ? { backgroundColor: "var(--color-primary)" }
                : {}
            }
          >
            Custom
          </button>
        </div>
        {form.customDuration && (
          <div className="mt-3">
            <input
              type="number"
              min={5}
              max={480}
              value={form.duration}
              onChange={(e) =>
                updateForm({
                  duration: Math.max(5, parseInt(e.target.value, 10) || 5),
                })
              }
              className="w-32 rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 transition-colors"
              placeholder="Minutes"
            />
            <span className="ml-2 text-sm text-slate-500">minutes</span>
          </div>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Description
        </label>
        <textarea
          value={form.description}
          onChange={(e) => updateForm({ description: e.target.value })}
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 transition-colors resize-none"
          rows={3}
          placeholder="Briefly describe what this meeting is about"
        />
      </div>

      {/* Location type */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Location
        </label>
        <div className="flex flex-wrap gap-2 mb-3">
          {LOCATION_TYPES.map((loc) => {
            const selected = form.locationType === loc.value;
            const LocIcon = loc.icon;
            return (
              <button
                key={loc.value}
                type="button"
                onClick={() => updateForm({ locationType: loc.value })}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${
                  selected
                    ? "text-white border-transparent shadow-sm"
                    : "border-slate-200 text-slate-600 hover:border-slate-300"
                }`}
                style={
                  selected
                    ? { backgroundColor: "var(--color-primary)" }
                    : {}
                }
              >
                <LocIcon className="h-4 w-4" />
                {loc.label}
              </button>
            );
          })}
        </div>
        <input
          type="text"
          value={form.locationDetails}
          onChange={(e) => updateForm({ locationDetails: e.target.value })}
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 transition-colors"
          placeholder={
            form.locationType === "video"
              ? "e.g., Zoom link or Google Meet"
              : form.locationType === "phone"
              ? "e.g., Phone number to call"
              : form.locationType === "in_person"
              ? "e.g., Office address"
              : "e.g., Custom location details"
          }
        />
      </div>
    </div>
  );

  // ── Step 2: Availability ──────────────────────────────────────────

  const addSlot = (dayKey: string) => {
    const schedule = form.availability[dayKey];
    const lastSlot = schedule.slots[schedule.slots.length - 1];
    const newStart = lastSlot?.endTime || "13:00";
    // Default new slot to 1 hour after start
    const [h, m] = newStart.split(":").map(Number);
    const endH = Math.min(h + 1, 23);
    const newEnd = `${String(endH).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    updateForm({
      availability: {
        ...form.availability,
        [dayKey]: {
          ...schedule,
          slots: [
            ...schedule.slots,
            { startTime: newStart, endTime: newEnd },
          ],
        },
      },
    });
  };

  const removeSlot = (dayKey: string, slotIndex: number) => {
    const schedule = form.availability[dayKey];
    updateForm({
      availability: {
        ...form.availability,
        [dayKey]: {
          ...schedule,
          slots: schedule.slots.filter((_, i) => i !== slotIndex),
        },
      },
    });
  };

  const updateSlot = (
    dayKey: string,
    slotIndex: number,
    field: "startTime" | "endTime",
    value: string
  ) => {
    const schedule = form.availability[dayKey];
    updateForm({
      availability: {
        ...form.availability,
        [dayKey]: {
          ...schedule,
          slots: schedule.slots.map((s, i) =>
            i === slotIndex ? { ...s, [field]: value } : s
          ),
        },
      },
    });
  };

  const renderStep2 = () => (
    <div className="space-y-6">
      {/* Calendar conflict check */}
      <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50/70 border border-slate-200">
        <div>
          <p className="text-sm font-medium text-slate-700">
            Check my calendar for conflicts
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            Automatically block times when you have existing events
          </p>
        </div>
        <Toggle
          checked={form.checkCalendarConflicts}
          onChange={(val) => updateForm({ checkCalendarConflicts: val })}
          size="sm"
        />
      </div>

      {/* Day schedule */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          When do you want this meeting type available?
        </label>
        <p className="text-xs text-slate-500 mb-3">
          Add multiple time slots per day to leave gaps (e.g. for lunch).
        </p>
        <div className="space-y-2">
          {DAY_LABELS.map((day) => {
            const schedule = form.availability[day.key];
            return (
              <div
                key={day.key}
                className="rounded-xl bg-slate-50/70 px-3 py-2"
              >
                {/* Day header row */}
                <div className="flex items-center gap-3">
                  <Toggle
                    checked={schedule.enabled}
                    onChange={(val) =>
                      updateForm({
                        availability: {
                          ...form.availability,
                          [day.key]: {
                            ...schedule,
                            enabled: val,
                            slots:
                              val && schedule.slots.length === 0
                                ? [{ startTime: "09:00", endTime: "17:00" }]
                                : schedule.slots,
                          },
                        },
                      })
                    }
                    size="sm"
                  />
                  <span
                    className={`text-sm font-medium w-10 ${
                      schedule.enabled ? "text-slate-700" : "text-slate-400"
                    }`}
                  >
                    {day.label}
                  </span>
                  {schedule.enabled ? (
                    <div className="flex flex-col gap-1.5 flex-1">
                      {schedule.slots.map((slot, si) => (
                        <div key={si} className="flex items-center gap-2 justify-end">
                          <TimePicker
                            value={slot.startTime}
                            onChange={(val) =>
                              updateSlot(day.key, si, "startTime", val)
                            }
                          />
                          <span className="text-slate-400 text-xs">to</span>
                          <TimePicker
                            value={slot.endTime}
                            onChange={(val) =>
                              updateSlot(day.key, si, "endTime", val)
                            }
                          />
                          {schedule.slots.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeSlot(day.key, si)}
                              className="p-1 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                              title="Remove time slot"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="ml-auto text-sm text-slate-400">
                      Unavailable
                    </span>
                  )}
                </div>
                {/* Add slot button */}
                {schedule.enabled && (
                  <div className="flex justify-end mt-1">
                    <button
                      type="button"
                      onClick={() => addSlot(day.key)}
                      className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
                    >
                      <Plus className="h-3 w-3" />
                      Add slot
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Buffers */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Buffer Time
        </label>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Before meeting (min)
            </label>
            <input
              type="number"
              min={0}
              max={120}
              value={form.bufferBefore}
              onChange={(e) =>
                updateForm({
                  bufferBefore: Math.max(0, parseInt(e.target.value, 10) || 0),
                })
              }
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 transition-colors"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              After meeting (min)
            </label>
            <input
              type="number"
              min={0}
              max={120}
              value={form.bufferAfter}
              onChange={(e) =>
                updateForm({
                  bufferAfter: Math.max(0, parseInt(e.target.value, 10) || 0),
                })
              }
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 transition-colors"
              placeholder="0"
            />
          </div>
        </div>
      </div>

      {/* Minimum notice */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Minimum Notice
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            max={720}
            value={form.minimumNotice}
            onChange={(e) =>
              updateForm({
                minimumNotice: Math.max(0, parseInt(e.target.value, 10) || 0),
              })
            }
            className="w-32 rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 transition-colors"
            placeholder="1"
          />
          <span className="text-sm text-slate-500">hours in advance</span>
        </div>
      </div>

      {/* Rolling window */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Rolling Window
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={365}
            value={form.rollingWindow}
            onChange={(e) =>
              updateForm({
                rollingWindow: Math.max(1, parseInt(e.target.value, 10) || 1),
              })
            }
            className="w-32 rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 transition-colors"
            placeholder="60"
          />
          <span className="text-sm text-slate-500">days into the future</span>
        </div>
      </div>

      {/* Max bookings per day */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Max Bookings Per Day
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={50}
            value={form.maxBookingsPerDay}
            onChange={(e) =>
              updateForm({ maxBookingsPerDay: e.target.value })
            }
            className="w-32 rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 transition-colors"
            placeholder="Unlimited"
          />
          <span className="text-sm text-slate-500">
            {form.maxBookingsPerDay ? "per day" : "(leave empty for unlimited)"}
          </span>
        </div>
      </div>

      {/* Timezone */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Timezone
        </label>
        <SearchableSelect
          options={TIMEZONE_OPTIONS}
          value={form.timezone}
          onChange={(val) => updateForm({ timezone: val || "America/Toronto" })}
          placeholder="Select timezone"
        />
      </div>
    </div>
  );

  // ── Step 3: Questions & Branding ──────────────────────────────────

  const addSuggestedQuestion = (sq: typeof SUGGESTED_QUESTIONS[0]) => {
    const newQ: CustomQuestion = {
      id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      label: sq.label,
      type: sq.type,
      required: sq.required,
      options: sq.options || [],
    };
    updateForm({ questions: [...form.questions, newQ] });
  };

  const renderStep3 = () => (
    <div className="space-y-6">
      {/* Default intake fields */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Intake Information
        </label>
        <p className="text-xs text-slate-500 mb-3">
          Name and email are always collected. Toggle additional fields below.
        </p>
        <div className="space-y-2">
          <div className="flex items-center justify-between py-2 px-3 rounded-xl bg-slate-50/70">
            <div className="flex items-center gap-2">
              <User className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-sm text-slate-700">Full Name</span>
            </div>
            <span className="text-xs text-slate-400 font-medium">Always collected</span>
          </div>
          <div className="flex items-center justify-between py-2 px-3 rounded-xl bg-slate-50/70">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-sm text-slate-700">Email Address</span>
            </div>
            <span className="text-xs text-slate-400 font-medium">Always collected</span>
          </div>
          <div className="flex items-center justify-between py-2 px-3 rounded-xl bg-slate-50/70">
            <div className="flex items-center gap-2">
              <Phone className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-sm text-slate-700">Phone Number</span>
            </div>
            <Toggle
              checked={form.collectPhone}
              onChange={(val) => updateForm({ collectPhone: val })}
              size="sm"
            />
          </div>
          <div className="flex items-center justify-between py-2 px-3 rounded-xl bg-slate-50/70">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-sm text-slate-700">Message / Notes</span>
            </div>
            <Toggle
              checked={form.collectMessage}
              onChange={(val) => updateForm({ collectMessage: val })}
              size="sm"
            />
          </div>
        </div>
      </div>

      {/* Suggested questions */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Suggested Questions for Law Firms
        </label>
        <p className="text-xs text-slate-500 mb-3">
          Click to add any of these pre-built questions to your booking form.
        </p>
        <div className="flex flex-wrap gap-2">
          {SUGGESTED_QUESTIONS.filter(
            (sq) => !form.questions.some((q) => q.label === sq.label)
          ).map((sq) => (
            <button
              key={sq.label}
              type="button"
              onClick={() => addSuggestedQuestion(sq)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-slate-300 text-xs font-medium text-slate-600 hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50/50 transition-colors"
            >
              <Plus className="h-3 w-3" />
              {sq.label.length > 45 ? sq.label.slice(0, 45) + "…" : sq.label}
            </button>
          ))}
          {SUGGESTED_QUESTIONS.every((sq) =>
            form.questions.some((q) => q.label === sq.label)
          ) && (
            <span className="text-xs text-slate-400 italic">
              All suggested questions added
            </span>
          )}
        </div>
      </div>

      {/* Custom Questions */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Custom Questions
        </label>
        <p className="text-xs text-slate-500 mb-3">
          Add your own questions for clients to answer when booking.
        </p>

        {form.questions.length > 0 && (
          <div className="space-y-4 mb-4">
            {form.questions.map((question) => (
              <div
                key={question.id}
                className="rounded-xl border border-slate-200 p-4 space-y-3"
              >
                {/* Label — full width */}
                <input
                  type="text"
                  value={question.label}
                  onChange={(e) =>
                    updateQuestion(question.id, { label: e.target.value })
                  }
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 transition-colors"
                  placeholder="Question label"
                />

                {/* Controls row */}
                <div className="flex items-center gap-3">
                  {/* Type selector */}
                  <div className="w-36">
                    <SearchableSelect
                      options={QUESTION_TYPES}
                      value={question.type}
                      onChange={(val) =>
                        updateQuestion(question.id, {
                          type: (val || "text") as CustomQuestion["type"],
                        })
                      }
                      placeholder="Type"
                    />
                  </div>
                  {/* Required toggle */}
                  <div className="flex items-center gap-1.5">
                    <Toggle
                      checked={question.required}
                      onChange={(val) =>
                        updateQuestion(question.id, { required: val })
                      }
                      size="sm"
                    />
                    <span className="text-xs text-slate-500">Required</span>
                  </div>
                  {/* Spacer */}
                  <div className="flex-1" />
                  {/* Delete */}
                  <button
                    type="button"
                    onClick={() => removeQuestion(question.id)}
                    className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* Dropdown options (if select type) */}
                {question.type === "select" && (
                  <div className="pl-1 space-y-2">
                    <label className="block text-xs font-medium text-slate-500">
                      Dropdown Options
                    </label>
                    {question.options.map((opt, optIdx) => (
                      <div key={optIdx} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={opt}
                          onChange={(e) =>
                            updateQuestionOption(
                              question.id,
                              optIdx,
                              e.target.value
                            )
                          }
                          className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 transition-colors"
                          placeholder={`Option ${optIdx + 1}`}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            removeQuestionOption(question.id, optIdx)
                          }
                          className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addQuestionOption(question.id)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add Option
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={addQuestion}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-slate-300 text-sm font-medium text-slate-600 hover:border-slate-400 hover:text-slate-700 transition-colors w-full justify-center"
        >
          <Plus className="h-4 w-4" />
          Add Question
        </button>
      </div>

      {/* Accent color */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Accent Color
        </label>
        <div className="flex items-center gap-2.5">
          {PRESET_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() =>
                updateForm({
                  accentColor: form.accentColor === color ? null : color,
                })
              }
              className={`h-7 w-7 rounded-full border-2 transition-all flex items-center justify-center ${
                form.accentColor === color
                  ? "border-slate-800 scale-110 shadow-md"
                  : "border-transparent hover:scale-105"
              }`}
              style={{ backgroundColor: color }}
            >
              {form.accentColor === color && (
                <Check className="h-3.5 w-3.5 text-white drop-shadow-sm" />
              )}
            </button>
          ))}
          {form.accentColor && (
            <button
              type="button"
              onClick={() => updateForm({ accentColor: null })}
              className="ml-1 p-1 rounded-lg hover:bg-slate-100 transition-colors"
              title="Remove color"
            >
              <X className="h-4 w-4 text-slate-400" />
            </button>
          )}
        </div>
      </div>

      {/* Confirmation message */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Confirmation Message
        </label>
        <textarea
          value={form.confirmationMessage}
          onChange={(e) =>
            updateForm({ confirmationMessage: e.target.value })
          }
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 transition-colors resize-none"
          rows={3}
          placeholder="Custom message shown after someone books (optional)"
        />
      </div>

      {/* Requires approval */}
      <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50/70">
        <div>
          <p className="text-sm font-medium text-slate-700">
            Requires Approval
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            Bookings require your approval before being confirmed
          </p>
        </div>
        <Toggle
          checked={form.requiresApproval}
          onChange={(val) => updateForm({ requiresApproval: val })}
        />
      </div>
    </div>
  );

  // ── Step 4: Review & Share ────────────────────────────────────────

  const renderStep4 = () => (
    <div className="space-y-5">
      {/* Summary card */}
      <div className="rounded-xl border border-slate-200 bg-slate-50/50 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <Settings2 className="h-4 w-4 text-slate-500" />
            Configuration Summary
          </h3>
        </div>
        <div className="px-5 py-4 space-y-3.5">
          {/* Name */}
          <div className="flex items-start justify-between">
            <span className="text-sm text-slate-500">Name</span>
            <span className="text-sm font-medium text-slate-800 text-right max-w-[60%]">
              {form.name || "--"}
            </span>
          </div>

          {/* Duration */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Duration</span>
            <span className="text-sm font-medium text-slate-800 inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-slate-400" />
              {form.duration} minutes
            </span>
          </div>

          {/* Location */}
          <div className="flex items-start justify-between">
            <span className="text-sm text-slate-500">Location</span>
            <span className="text-sm font-medium text-slate-800 text-right max-w-[60%] inline-flex items-center gap-1.5">
              {(() => {
                const loc = LOCATION_TYPES.find(
                  (l) => l.value === form.locationType
                );
                const LocIcon = loc?.icon || MapPin;
                return (
                  <>
                    <LocIcon className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                    {loc?.label || form.locationType}
                    {form.locationDetails && (
                      <span className="text-slate-500">
                        ({form.locationDetails})
                      </span>
                    )}
                  </>
                );
              })()}
            </span>
          </div>

          {/* Availability */}
          <div className="flex items-start justify-between">
            <span className="text-sm text-slate-500">Availability</span>
            <span className="text-sm font-medium text-slate-800 text-right max-w-[60%]">
              {availabilitySummary}
            </span>
          </div>

          {/* Timezone */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Timezone</span>
            <span className="text-sm font-medium text-slate-800">
              {form.timezone}
            </span>
          </div>

          {/* Buffer */}
          {(form.bufferBefore > 0 || form.bufferAfter > 0) && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Buffer</span>
              <span className="text-sm font-medium text-slate-800">
                {form.bufferBefore > 0 && `${form.bufferBefore}min before`}
                {form.bufferBefore > 0 && form.bufferAfter > 0 && ", "}
                {form.bufferAfter > 0 && `${form.bufferAfter}min after`}
              </span>
            </div>
          )}

          {/* Questions */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Questions</span>
            <span className="text-sm font-medium text-slate-800 inline-flex items-center gap-1.5">
              <MessageSquare className="h-3.5 w-3.5 text-slate-400" />
              {form.questions.length === 0
                ? "None"
                : `${form.questions.length} question${
                    form.questions.length > 1 ? "s" : ""
                  }`}
            </span>
          </div>

          {/* Approval */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Approval Required</span>
            <span
              className={`text-sm font-medium ${
                form.requiresApproval ? "text-amber-600" : "text-slate-800"
              }`}
            >
              {form.requiresApproval ? "Yes" : "No"}
            </span>
          </div>

          {/* Accent color */}
          {form.accentColor && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Accent Color</span>
              <span
                className="h-5 w-5 rounded-full border border-slate-200"
                style={{ backgroundColor: form.accentColor }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Booking link */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Booking Link
        </label>
        <div className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 bg-white">
          <span className="flex-1 text-sm text-slate-600 truncate font-mono min-w-0">
            {bookingUrl}
          </span>
          <button
            type="button"
            onClick={handleCopyLink}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5 text-emerald-600" />
                <span className="text-emerald-600">Copied</span>
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copy
              </>
            )}
          </button>
          {editId && (
            <button
              type="button"
              onClick={() =>
                window.open(bookingUrl, "_blank")
              }
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Preview
            </button>
          )}
        </div>
        {!editId && (
          <p className="mt-1.5 text-xs text-slate-400">
            Save to activate this link. You can preview it after saving.
          </p>
        )}
      </div>
    </div>
  );

  // ── Footer ────────────────────────────────────────────────────────

  const renderFooter = () => (
    <div className="flex items-center justify-between">
      <div>
        {step > 1 && (
          <button
            type="button"
            onClick={() => setStep((s) => s - 1)}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Back
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
        >
          Cancel
        </button>
        {step < 4 ? (
          <button
            type="button"
            disabled={!canAdvance}
            onClick={() => setStep((s) => s + 1)}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-colors disabled:opacity-50"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => {
              if (canAdvance) {
                e.currentTarget.style.backgroundColor =
                  "var(--color-primary-hover)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "var(--color-primary)";
            }}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            className="px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-colors disabled:opacity-50"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => {
              if (!saving) {
                e.currentTarget.style.backgroundColor =
                  "var(--color-primary-hover)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "var(--color-primary)";
            }}
          >
            {saving
              ? isEditing
                ? "Saving..."
                : "Creating..."
              : isEditing
              ? "Save Changes"
              : "Create Meeting Type"}
          </button>
        )}
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      title={isEditing ? "Edit Booking Link" : "New Booking Link"}
      subtitle={
        isEditing
          ? "Update your booking link settings"
          : "Create a shareable link so clients can book time with you — like Calendly, built into your calendar"
      }
      icon={CalendarClock}
      size="lg"
      footer={renderFooter()}
    >
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="h-8 w-8 text-emerald-600 animate-spin mb-3" />
          <p className="text-sm text-slate-500">Loading booking type...</p>
        </div>
      ) : (
        <>
          {renderStepper()}
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </>
      )}
    </PremiumModal>
  );
};

export default BookingTypeModal;
